import {getChildElementAlignment, tryExecution} from '../_util';
import themeDropdownMenuNested from './_dropdownMenuNested';
import {debounce} from 'lodash-es';

const baseClass = 'nav-pills';
const itemBaseClass = 'nav-item';

const slidableBreakpoint = 767;

class NavPillsOverflow {
  /**
   * @type {HTMLElement}
   * @private
   */
  _el;

  /**
   * @type {HTMLElement|null}
   * @private
   */
  _overflowMenuEl = null;

  /**
   * @type {DropdownMenuNested|null}
   * @private
   */
  _overflowMenu = null;

  /**
   * @type {ResizeObserver}
   * @private
   */
  _resizeObserver;

  /**
   * @type {() => void}
   * @private
   */
  _resizeHandler;

  /**
   * @type {boolean}
   * @private
   */
  _overflowed = false;

  constructor(el) {
    this._el = el;

    this._resizeHandler = debounce(this.handleResize.bind(this), 100);

    this.fitItemsIntoMenu();

    this._resizeObserver = new ResizeObserver(this._resizeHandler);
    this._resizeObserver.observe(this._el);

    this._el.classList.add(`${baseClass}--initialized`);
  }

  addItemsToOverflowNavigation() {
    const items = this._el.getElementsByClassName('nav-item');
    const itemsWithoutOverflow = Array.from(items)
                                       .filter(x => x.classList.contains(`${itemBaseClass}--overflow-menu`) === false);

    for (const item of itemsWithoutOverflow.reverse()) {
      this.addItemToOverflowNavigation(item);
    }
  }

  addItemToOverflowNavigation(item) {
    const isHidden = item.classList.contains('d-none');
    if (item.classList.contains('dropdown')) {
      const dropdownToggle = item.querySelector('.dropdown-toggle');

      this._overflowMenu.mergeDropdownIntoDropdown(
        dropdownToggle.innerText,
        dropdownToggle.classList.contains('active'),
        isHidden,
        item.querySelector('.dropdown-menu')
      );

      return;
    }

    function addClassesToElement(element) {
      const linkItem = element.classList.contains('nav-link');

      if (linkItem) {
        element.classList.remove('nav-link');
      }

      element.classList.add(...[
        linkItem ? 'dropdown-item' : null,
        'dropdown-item--overflow-item',
        isHidden ? 'dropdown-item--overflow-visible' : null,
      ].filter(x => x != null));

      return element;
    }

    let element = item.querySelector('.nav-link');

    if (element == null) {
      element = item.querySelector('.dropdown-divider');
    }

    if (element == null) {
      console.warn('Unable to find copyable element in ', item);

      return;
    }

    this._overflowMenu.addElementToFirstGroup(addClassesToElement(element.cloneNode(true)));
  }

  doItemsOverlap() {
    const { left, right } = this._el.getBoundingClientRect();

    switch (getChildElementAlignment(this._el)) {
      case "left":
        if (this._overflowMenuEl != null && this._overflowed) {
          return right < this._overflowMenuEl.getBoundingClientRect().right;
        }

        const visibleItems = this.visibleNormalItems;

        return right < visibleItems[visibleItems.length - 1].getBoundingClientRect().right;

      case "right":
        const firstVisibleItem = this.visibleNormalItems[0];

        return left > firstVisibleItem.getBoundingClientRect().left;

      default:
        return true;
    }
  }

  fitItemsIntoMenu() {
    // Revert previous fits
    for (const hiddenItems of this.hiddenNormalItems) {
      hiddenItems.classList.remove('d-none');
    }

    for (const visibleOverflowItem of this.visibleOverflowItems) {
      visibleOverflowItem.classList.remove('dropdown-item--overflow-visible');
    }

    this.overflowed = false;

    while (this.doItemsOverlap()) {

      this.overflowed = true;

      const visibleItems = this.visibleNormalItems;
      const lastItemIndex = visibleItems.length - 1;

      const lastItem = visibleItems[lastItemIndex];
      lastItem.classList.add('d-none');

      this.overflowItems[lastItemIndex]?.classList.add('dropdown-item--overflow-visible');
    }
  }

  handleResize() {
    this.fitItemsIntoMenu();
  }

  initializeOverflowMenu() {
    if (this._overflowMenuEl != null) {
      return;
    }

    this._overflowMenuEl = this.addDropdownOverflowMenu();
    if (this._overflowMenuEl == null) {
      console.warn('Unable to find overflow menu with class', `.${itemBaseClass}--overflow-menu`, 'in', this._el);

      return;
    }

    this._overflowMenu = themeDropdownMenuNested.createMenuInstance(this._overflowMenuEl.querySelector('.dropdown-menu'));
    this._overflowMenu.onOpenDropdown(this.addItemsToOverflowNavigation.bind(this));
  }

  /**
   * @return {ChildNode}
   */
  addDropdownOverflowMenu() {
    const element = document.createElement('div');

    element.innerHTML = '<li class="nav-item dropdown nav-item--overflow-menu  nav-item--overflow-menu-visible">\n' +
      '        <a class="nav-link dropdown-toggle" data-bs-toggle="dropdown" href="#" role="button" aria-expanded="false">Mehr</a>\n' +
      '        <div class="dropdown-menu dropdown-menu--nested">\n' +
      '            <div class="dropdown-menu__column">\n' +
      '                <div class="dropdown-menu__group">\n' +
      '                </div>\n' +
      '            </div>\n' +
      '        </div>\n' +
      '    </li>';

    const result = element.firstChild;

    this._el.append(result);

    return result;
  }

  get normalItems() {
    const items = this._el.getElementsByClassName(itemBaseClass);

    return Array.from(items).filter(x => x.classList.contains(`${itemBaseClass}--overflow-menu`) === false);
  }

  get visibleNormalItems() {
    return this.normalItems.filter(x => x.classList.contains('d-none') === false);
  }

  get hiddenNormalItems() {
    return this.normalItems.filter(x => x.classList.contains('d-none') !== false);
  }

  get overflowItems() {
    if (this._overflowMenuEl == null) {
      return [];
    }

    return this._overflowMenuEl.getElementsByClassName('dropdown-item--overflow-item');
  }

  get visibleOverflowItems() {
    if (this._overflowMenuEl == null) {
      return [];
    }

    return this._overflowMenuEl.querySelectorAll('.dropdown-item--overflow-item.dropdown-item--overflow-visible');
  }

  get overflowed() {
    return this._overflowed;
  }

  set overflowed(value) {
    if (this._overflowed === value) {
      return;
    }

    if (value) {
      this.initializeOverflowMenu();
      this._overflowMenuEl.classList.add(`${itemBaseClass}--overflow-menu-visible`);
    } else {
      this._overflowMenuEl.classList.remove(`${itemBaseClass}--overflow-menu-visible`);
    }

    this._overflowed = value;
  }
}

function init() {
  const overflowingNavigations = document.getElementsByClassName(`${baseClass}--hide-overflow`);

  for (const overflowingNavigation of overflowingNavigations) {
    tryExecution(() => new NavPillsOverflow(overflowingNavigation));
  }
}

export default {
  init
};
